<template>
  <div>
    <div class="mb-3 text-indigo-medium text-uppercase coro-label">
      {{ stepperText }}
    </div>
    <div class="d-flex align-center justify-space-between">
      <v-progress-linear
        class="progress-bar-header-item"
        color="green-dark"
        :model-value="100"
        :max="100"
        rounded
      />
      <v-progress-linear
        :model-value="100"
        :max="100"
        class="progress-bar-header-item"
        :color="step === 2 ? 'green-dark' : 'indigo-pale'"
        rounded
      />
    </div>
    <div class="mt-3">
      <template v-if="step === 1">
        <div class="subtitle1 mb-1 mt-5">
          {{ $t("setupHub.subSteps.deployEndpointAgent.steps.initialStep.title") }}
        </div>
        <div class="body2 text-indigo-medium mb-4">
          {{ $t("setupHub.subSteps.deployEndpointAgent.steps.initialStep.subtitle") }}
        </div>
        <div class="mb-2" v-for="item in initialStepActions" :key="item.action">
          <setup-hub-option-card
            @click="initialStepCallback(item.action)"
            data-testid="deploy-agents-content-deploy-type-card"
          >
            <div>
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <v-icon :icon="item.icon" size="24" color="primary" />
                  <div class="ml-1 subtitle2 text-primary">
                    {{
                      $t(
                        `setupHub.subSteps.deployEndpointAgent.steps.initialStep.${item.action}.title`
                      )
                    }}
                  </div>
                </div>

                <v-chip
                  v-if="item.recommended"
                  variant="flat"
                  size="x-small"
                  color="indigo-sky"
                  class="recommended-chip"
                  label
                  rounded="xs"
                >
                  <v-icon icon="$star" size="14" />
                  <span class="ml-1 text-primary text-uppercase coro-label-sm">
                    {{ $t("setupHub.recommended") }}
                  </span>
                </v-chip>
              </div>

              <div class="body3 text-indigo-medium">
                {{
                  $t(
                    `setupHub.subSteps.deployEndpointAgent.steps.initialStep.${item.action}.description`
                  )
                }}
              </div>
            </div>
          </setup-hub-option-card>
        </div>
        <div class="d-flex align-center mt-5" @click="onHelpLinkClick()">
          <div class="mr-1 body2 coro-link">{{ $t("setupHub.readHelpArticle") }}</div>
          <v-icon icon="$externalLink" size="14" />
        </div>
      </template>
      <template v-else>
        <div class="subtitle1 mb-1 mt-5">
          {{ $t(`setupHub.subSteps.deployEndpointAgent.steps.${selectedAction}.title`) }}
        </div>
        <div class="body2 text-indigo-medium mb-4">
          {{ $t(`setupHub.subSteps.deployEndpointAgent.steps.${selectedAction}.subtitle`) }}
        </div>
        <div class="mb-2" v-for="item in endpointVersions" :key="item.id">
          <setup-hub-option-card
            data-testid="deploy-agents-content-os-version-option"
            :bg-color="'rgb(var(--v-theme-indigo-faint))'"
            :border-color="'rgb(var(--v-theme-indigo-faint))'"
            @click="onActionClick(selectedAction, item)"
          >
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon :icon="osTypeToIconMap[item.osType]" size="25" class="os-icon" />
                <div class="ml-2 subtitle2">
                  {{ $t(`setupHub.subSteps.deployEndpointAgent.${item.osType}`) }}
                </div>
                <div class="body3 ml-2 text-indigo-medium">
                  {{
                    $t("setupHub.subSteps.deployEndpointAgent.latestVersion", {
                      version: item.marketingVersion,
                    })
                  }}
                </div>
              </div>

              <v-icon
                v-if="selectedAction === AgentDeploymentAction.DOWNLOAD"
                icon="$export"
                size="24"
                class="action-icon"
              />

              <v-icon v-else icon="$trailingLink" size="24" class="action-icon" />
            </div>
          </setup-hub-option-card>
        </div>

        <div v-if="selectedAction === AgentDeploymentAction.DOWNLOAD">
          <div class="subtitle2 mt-6">
            {{
              $t(`setupHub.subSteps.deployEndpointAgent.steps.${selectedAction}.deployingWithRMM`)
            }}
          </div>
          <div class="body2 text-indigo-medium mt-2">
            {{
              $t(
                `setupHub.subSteps.deployEndpointAgent.steps.${selectedAction}.deployingWithRMMDescription`
              )
            }}
          </div>
          <div class="d-flex align-center mt-2" @click="onRmmLinkClick()">
            <div class="mr-1 body2 coro-link">
              {{ $t(`setupHub.subSteps.deployEndpointAgent.steps.${selectedAction}.learnMore`) }}
            </div>
            <v-icon icon="$externalLink" size="14" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  type DeviceVersionItem,
  useDevicesSettingsStore,
} from "@/_store/devices-settings.module.ts";
import { storeToRefs } from "pinia";
import { getLatestVersionsByOsType } from "@/_helpers/utils.ts";
import { onAgentDeploymentActionClick } from "@/_helpers/dialog-helpers.ts";
import SetupHubOptionCard from "@/components/setup-hub/shared/SetupHubOptionCard.vue";
import { AgentDeploymentAction, OsType } from "@/constants/devices.ts";
import { useSidebarPanelStore } from "@/_store/sidebar-panel.module.ts";

export default defineComponent({
  components: { SetupHubOptionCard },
  setup() {
    const step = ref(1);
    const i18n = useI18n();
    const selectedAction = ref(AgentDeploymentAction.DOWNLOAD);
    const devicesSettingsStore = useDevicesSettingsStore();
    const sidebarPanelStore = useSidebarPanelStore();
    const { availableVersions, downloadClientPageLink } = storeToRefs(devicesSettingsStore);

    const initialStepCallback = (action: AgentDeploymentAction) => {
      step.value = 2;
      selectedAction.value = action;
    };

    const endpointVersions = computed(() => {
      return getLatestVersionsByOsType(availableVersions.value);
    });

    const stepperText = computed(() => {
      return step.value === 1
        ? i18n.t("setupHub.subSteps.deployEndpointAgent.stepper.initialStep")
        : i18n.t("setupHub.subSteps.deployEndpointAgent.stepper.chooseOperatingSystem");
    });

    const onRmmLinkClick = () => {
      window.open("https://docs.coro.net/devices/agent-deploy/", "_blank");
    };

    const onHelpLinkClick = () => {
      window.open("https://docs.coro.net/agent/intro/", "_blank");
    };

    const initialStepActions = [
      {
        action: AgentDeploymentAction.DOWNLOAD,
        recommended: true,
        icon: "icon-endpoint-xs",
      },
      {
        action: AgentDeploymentAction.COPY_LINK,
        recommended: false,
        icon: "icon-link-xs",
      },
    ];

    const osTypeToIconMap = {
      [OsType.WINDOWS]: "icon-windows-logo-s",
      [OsType.LINUX]: "icon-linux-xs",
      [OsType.OSX]: "icon-apple-xs",
    };

    async function onActionClick(action: AgentDeploymentAction, item: DeviceVersionItem) {
      await onAgentDeploymentActionClick(action, item, downloadClientPageLink.value);
      if (action === AgentDeploymentAction.COPY_LINK) {
        sidebarPanelStore.closeSidebar();
      }
    }

    return {
      step,
      AgentDeploymentAction,
      initialStepActions,
      selectedAction,
      initialStepCallback,
      endpointVersions,
      osTypeToIconMap,
      stepperText,
      onRmmLinkClick,
      onHelpLinkClick,
      onActionClick,
    };
  },
});
</script>
<style scoped lang="scss">
.progress-bar-header-item {
  width: 49%;
}

.recommended-chip {
  margin-top: -15px;
  margin-right: -5px;
}

.os-icon::before {
  color: rgb(var(--v-theme-indigo-purple)) !important;
}

.action-icon::before {
  color: rgb(var(--v-theme-indigo-medium)) !important;
}
</style>
